import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/system";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

// Array labels render as multiple lines (Chart.js native multi-line ticks),
// e.g. ["Hissar GRPL", "(1907024)"] puts the hub code under the name.
const normalizeLabel = (label) => {
  if (Array.isArray(label)) return label;
  return label ?? "";
};

const styles = {
  container: "w-full max-w-[1400px]",
  graphWrapper: "w-full relative group mt-2",
  headerWrapper: "flex flex-col justify-between items-start mb-6 gap-4 px-2",
  titleWrapper: "mb-4",
  legendActionRow: "flex items-center justify-between gap-4 mb-3",
  legendBox:
    "flex flex-wrap items-center gap-3 rounded-[4px] px-2 py-2",
  breadcrumbsRow:
    "flex flex-row justify-between items-center w-full gap-4",
  breadcrumbsRail:
    "flex items-center gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide flex-1",
  chartOuter: "h-full overflow-y-auto overflow-x-hidden pr-1",
};

const MAX_VISIBLE_BARS = 7;
const MIN_GAP_BETWEEN_BARS = 6;

const getYCategoryIndexFromAxisClick = (
  event,
  chart,
  leftPadding = 12,
  rightPadding = 16,
) => {
  const yScale = chart?.scales?.y;
  const labels = chart?.data?.labels;
  const chartArea = chart?.chartArea;
  if (!yScale || !labels?.length || !chartArea) return -1;

  const raw = event?.native ?? event;
  const pos = getRelativePosition(raw, chart);
  if (!pos) return -1;

  const { x, y } = pos;
  // Keep click target on the y-axis label rail.
  const leftAxisBandStart = 0;
  const leftAxisBandEnd = yScale.right + rightPadding;

  if (x < leftAxisBandStart || x > leftAxisBandEnd) return -1;
  if (y < chartArea.top || y > chartArea.bottom) return -1;

  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = 0; i < labels.length; i++) {
    const py = yScale.getPixelForTick(i);
    if (py == null || Number.isNaN(py)) continue;
    const dist = Math.abs(y - py);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }

  return bestIdx;
};

export const CustomHorizontalBarChart = ({
  title,
  titleTextSize = "16px",
  breadcrumbs = [],
  labels = [],
  datasets = [],
  legendItems = [],
  legendPlacement = "belowTitle",
  xAxisTitle,
  onCategoryClick,
  onBarClick,
  hoverCursor = false,
  topRightAction,
  topRightActionPlacement = "legendActionRow", // "legendActionRow" (default) or "breadcrumbs"
  paddingTop = "24px",
  paddingRight = "24px",
  paddingBottom = "24px",
  paddingLeft = "24px",
  cardMinHeight,
  chartHeight = "440px",
  xAxisMax,
  xAxisStepSize,
  xAxisFormatter,
  fixedXAxis = false,
  fixedXAxisTicks,
  yAxisTitle = "Zones",
  showYAxisTitle = true,
  stacked = true,
  datalabelsFormatter,
  datalabelAnchor = "center",
  datalabelAlign = "center",
  datalabelColor = "#374151",
  datalabelHoverColor = "#4841B9",
  showPercentInTooltip = false,
  showCountInTooltip = false,
  tooltipDecimalPlaces = 2,
  /** Delay (ms) before hiding when the cursor leaves the chart hover target; use this to move onto the tooltip and click links. */
  tooltipChartDismissDelayMs = 700,
  /** Delay (ms) before hiding when the cursor leaves the tooltip box. */
  tooltipPointerLeaveDelayMs = 1000,
  barThickness = 30,
  /** Minimum on-screen length (px) for a non-zero bar segment so tiny values (e.g. 1-10) stay visible/clickable. 0 disables. */
  minBarLength = 10,
  yAxisClickBandLeftPadding = 12,
  yAxisClickBandRightPadding = 16,
  initialVerticalScrollPosition = "top",
  /** When set, draws a text label at the right end of each grouped stack.
   *  Shape: [{ stack: "scrap", label: "Scrap", color: "#6B7280" }, ...] */
  stackEndLabels,
}) => {
  const categoryClickHandlerRef = useRef(onCategoryClick);
  const chartScrollRef = useRef(null);
  const [chartAreaBounds, setChartAreaBounds] = useState({ left: 0, right: 0 });
  const chartAreaBoundsRef = useRef({ left: 0, right: 0 });

  const tooltipRef = useRef(null);
  const [tooltipEl, setTooltipEl] = useState(null);
  const hideTooltipTimeoutRef = useRef(null);
  const tooltipLeaveHideTimeoutRef = useRef(null);
  const tooltipHoveredRef = useRef(false);

  const setTooltipRef = useCallback((node) => {
    if (node) {
      tooltipRef.current = node;
      setTooltipEl(node);
    }
  }, []);

  useEffect(() => {
    categoryClickHandlerRef.current = onCategoryClick;
  }, [onCategoryClick]);

  // A click on a datalabel sits on top of the bar, so both the datalabels
  // plugin's click listener and Chart.js' options.onClick fire for the same
  // native event — only the first dispatch per native event reaches onBarClick.
  const barClickGuardRef = useRef(false);
  const dispatchBarClick = useCallback(
    (event, elements) => {
      if (!onBarClick) return;
      const native = event?.native ?? event;
      if (native && typeof native === "object") {
        if (native.__barClickDispatched) return;
        native.__barClickDispatched = true;
      }
      // Backstop: both handlers run synchronously for one native click, so a
      // same-tick second dispatch is always a duplicate (even without `native`).
      if (barClickGuardRef.current) return;
      barClickGuardRef.current = true;
      setTimeout(() => {
        barClickGuardRef.current = false;
      }, 0);
      // Deferred: React 17 flushes state updates from native listeners
      // synchronously, so a handler that unmounts this chart would destroy it
      // while Chart.js is still mid-event and it then renders with a null ctx
      // ("Cannot read properties of null (reading 'save')").
      const elementsSnapshot = Array.isArray(elements) ? [...elements] : elements;
      setTimeout(() => onBarClick(event, elementsSnapshot), 0);
    },
    [onBarClick],
  );

  useEffect(() => {
    const el = tooltipEl;
    if (!el) return;

    const handleMouseEnter = () => {
      tooltipHoveredRef.current = true;
      if (tooltipLeaveHideTimeoutRef.current) {
        clearTimeout(tooltipLeaveHideTimeoutRef.current);
        tooltipLeaveHideTimeoutRef.current = null;
      }
    };
    const handleMouseLeave = () => {
      tooltipHoveredRef.current = false;
      if (tooltipLeaveHideTimeoutRef.current) {
        clearTimeout(tooltipLeaveHideTimeoutRef.current);
      }
      tooltipLeaveHideTimeoutRef.current = setTimeout(() => {
        tooltipLeaveHideTimeoutRef.current = null;
        if (!tooltipHoveredRef.current) {
          el.style.opacity = 0;
          el.style.pointerEvents = 'none';
        }
      }, tooltipPointerLeaveDelayMs);
    };
    const handleClick = (e) => {
      const target = e.target.closest('.tooltip-interactive-text');
      if (target && onBarClick) {
        const datasetIndex = parseInt(target.getAttribute('data-dataset'), 10);
        const dataIndex = parseInt(target.getAttribute('data-index'), 10);
        dispatchBarClick(e, [{ index: dataIndex, datasetIndex: datasetIndex }]);
      }
    };

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);
    el.addEventListener('click', handleClick);

    return () => {
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
      el.removeEventListener('click', handleClick);
      if (hideTooltipTimeoutRef.current) clearTimeout(hideTooltipTimeoutRef.current);
      if (tooltipLeaveHideTimeoutRef.current) {
        clearTimeout(tooltipLeaveHideTimeoutRef.current);
        tooltipLeaveHideTimeoutRef.current = null;
      }
    };
  }, [onBarClick, dispatchBarClick, tooltipEl, tooltipPointerLeaveDelayMs]);

  // Memoized so the chart `data` object keeps its identity across re-renders —
  // a fresh labels array every render forces react-chartjs-2 to chart.update()
  // (visible bar repaint) whenever the parent re-renders, e.g. on modal open.
  const normalizedLabels = useMemo(
    () => (labels || []).map(normalizeLabel),
    [labels],
  );
  const shouldScrollY = normalizedLabels.length > MAX_VISIBLE_BARS;
  const viewportHeightPx = Number.parseFloat(String(chartHeight)) || 440;
  const barSlotHeight = Math.max(
    Number(barThickness || 0) + MIN_GAP_BETWEEN_BARS,
    viewportHeightPx / MAX_VISIBLE_BARS,
  );
  const innerChartHeightPx =
    normalizedLabels.length > MAX_VISIBLE_BARS
      ? normalizedLabels.length * barSlotHeight
      : viewportHeightPx;

  useEffect(() => {
    const node = chartScrollRef.current;
    if (!node) return;
    if (initialVerticalScrollPosition === "bottom") {
      node.scrollTop = node.scrollHeight;
      return;
    }
    node.scrollTop = 0;
  }, [initialVerticalScrollPosition, normalizedLabels.length, innerChartHeightPx]);

  const data = useMemo(
    () => ({
      labels: normalizedLabels,
      datasets: (datasets || []).map((ds) => ({
        ...ds,
        barThickness: ds.barThickness ?? barThickness,
      })),
    }),
    [normalizedLabels, datasets, barThickness],
  );

  const yAxisCategoryPlugin = useMemo(
    () => ({
      id: "yAxisCategoryInteraction",
      afterEvent(chart, args) {
        const { event, replay } = args;
        const latestCategoryClickHandler = categoryClickHandlerRef.current;
        if (replay || !event?.native || !latestCategoryClickHandler) return;

        const nativeType = event.native.type;
        if (nativeType !== "click" && nativeType !== "mousemove") return;

        // Keep bar click behavior separate from y-axis drilldown behavior.
        const barElements = chart.getElementsAtEventForMode(
          event,
          "nearest",
          { intersect: true },
          false,
        );
        if (barElements?.length) return;

        const idx = getYCategoryIndexFromAxisClick(
          event,
          chart,
          yAxisClickBandLeftPadding,
          yAxisClickBandRightPadding,
        );
        if (nativeType === "click") {
          // Deferred: the drilldown handler unmounts this chart (loading
          // spinner) and React 17 flushes that synchronously — destroying the
          // chart mid-event makes Chart.js render with a null ctx.
          if (idx >= 0) {
            setTimeout(
              () => latestCategoryClickHandler(event, [{ index: idx }]),
              0,
            );
          }
          return;
        }

        if (nativeType === "mousemove" && chart.canvas) {
          chart.canvas.style.cursor = idx >= 0 ? "pointer" : "default";
        }
      },
    }),
    [yAxisClickBandLeftPadding, yAxisClickBandRightPadding],
  );

  const chartAreaSyncPlugin = useMemo(
    () => ({
      id: "chartAreaSync",
      afterLayout(chart) {
        const area = chart?.chartArea;
        if (!area) return;
        const next = { left: area.left, right: area.right };
        const prev = chartAreaBoundsRef.current;
        if (
          Math.abs(prev.left - next.left) > 0.5 ||
          Math.abs(prev.right - next.right) > 0.5
        ) {
          chartAreaBoundsRef.current = next;
          setChartAreaBounds(next);
        }
      },
    }),
    [],
  );

  // Enforce a minimum on-screen length for non-zero (horizontal) bar segments so
  // small values like 1-10 stay visible and clickable. Chart.js' built-in
  // `minBarLength` would also draw slivers for zero segments and let larger
  // segments overlap the bumped small ones, so we adjust geometry ourselves and
  // shift the rest of the stack along. We update animation targets too so the
  // change survives the grow animation.
  const minBarLengthPlugin = useMemo(
    () => ({
      id: "minStackedBarLength",
      afterDatasetsUpdate(chart) {
        const minPx = Number(minBarLength) || 0;
        if (minPx <= 0) return;

        const targetOf = (el, prop) =>
          el.$animations &&
          el.$animations[prop] &&
          el.$animations[prop]._to !== undefined
            ? el.$animations[prop]._to
            : el[prop];
        const setProp = (el, prop, val) => {
          el[prop] = val;
          if (el.$animations && el.$animations[prop]) {
            el.$animations[prop]._to = val;
          }
        };

        const datasets = chart.data.datasets || [];
        const labelCount = (chart.data.labels || []).length;

        for (let i = 0; i < labelCount; i++) {
          // Offsets accumulate per stack group: with side-by-side stacks (e.g.
          // scrap vs casing in "All" mode) each bar is independent, so a bump in
          // one stack must not shift the other stack off the axis.
          const offsets = {};
          for (let d = 0; d < datasets.length; d++) {
            const meta = chart.getDatasetMeta(d);
            if (!meta || meta.hidden) continue;
            const el = meta.data && meta.data[i];
            if (!el || el.horizontal !== true) continue;

            const value = Number(datasets[d].data[i]);
            if (!Number.isFinite(value) || value <= 0) continue;

            let base = targetOf(el, "base");
            let x = targetOf(el, "x");
            if (!Number.isFinite(base) || !Number.isFinite(x) || x < base) {
              continue;
            }

            const stackKey = datasets[d].stack ?? "__default__";
            // Bars grow rightward (base -> x). Slide this segment past anything
            // we've already widened in the same stacked category.
            if (stacked) {
              const offset = offsets[stackKey] || 0;
              base += offset;
              x += offset;
            }
            let width = x - base;
            if (width < minPx) {
              const grow = minPx - width;
              x += grow;
              if (stacked) offsets[stackKey] = (offsets[stackKey] || 0) + grow;
              width = minPx;
            }

            setProp(el, "base", base);
            setProp(el, "x", x);
            setProp(el, "width", width);
          }
        }
      },
    }),
    [minBarLength, stacked],
  );

  // Draws a "Scrap"/"Casing" text label just past the right end of each grouped
  // stack so the two bars in a category can be told apart at a glance. Runs after
  // minBarLengthPlugin so it reads the final (possibly bumped) bar geometry.
  const stackEndLabelPlugin = useMemo(
    () => ({
      id: "stackEndLabels",
      afterDatasetsDraw(chart) {
        const cfg = stackEndLabels;
        if (!Array.isArray(cfg) || cfg.length === 0) return;

        const { ctx } = chart;
        const datasets = chart.data.datasets || [];
        const labelCount = (chart.data.labels || []).length;
        const areaRight = chart.chartArea?.right ?? chart.width;

        ctx.save();
        ctx.font = "600 11px 'Inter', sans-serif";
        ctx.textBaseline = "middle";

        for (const entry of cfg) {
          const dsIdxs = [];
          datasets.forEach((ds, di) => {
            if (ds.stack === entry.stack) dsIdxs.push(di);
          });
          if (!dsIdxs.length) continue;

          for (let i = 0; i < labelCount; i++) {
            let maxX = -Infinity;
            let y = null;
            for (const di of dsIdxs) {
              const meta = chart.getDatasetMeta(di);
              if (!meta || meta.hidden) continue;
              const el = meta.data && meta.data[i];
              if (!el) continue;
              const value = Number(datasets[di].data[i]);
              if (!Number.isFinite(value) || value === 0) continue;
              if (el.x > maxX) maxX = el.x;
              y = el.y;
            }
            if (y == null || maxX === -Infinity) continue;

            ctx.fillStyle = entry.color || "#6B7280";
            const text = entry.label ?? "";
            const textWidth = ctx.measureText(text).width;
            // Keep the label inside the chart area; if it would overflow on the
            // right, draw it ending at the bar tip instead of starting there.
            if (maxX + 6 + textWidth <= areaRight) {
              ctx.textAlign = "left";
              ctx.fillText(text, maxX + 6, y);
            } else {
              ctx.textAlign = "right";
              ctx.fillText(text, Math.min(maxX - 6, areaRight - 2), y);
            }
          }
        }

        ctx.restore();
      },
    }),
    [stackEndLabels],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      onClick: (event, elements) => {
        if (elements?.length && onBarClick) {
          dispatchBarClick(event, elements);
        }
      },
      onHover: (event, elements, chart) => {
        const target = event.native?.target;
        if (!target) return;
        if (chart.isDataLabelHovered) {
          target.style.cursor = 'pointer';
          return;
        }
        target.style.cursor = elements?.length && (onCategoryClick || hoverCursor || onBarClick)
          ? "pointer"
          : "default";
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          filter: (tooltipItem) => {
            const v = tooltipItem.parsed?.x;
            return v != null && Number(v) !== 0;
          },
          external: (context) => {
            const { chart, tooltip } = context;
            const tooltipEl = tooltipRef.current;
            if (!tooltipEl) return;

            const nonZeroPts = tooltip?.dataPoints?.filter(
              (dp) => dp.parsed?.x != null && Number(dp.parsed.x) !== 0
            );
            // Hide if no tooltip, opacity is 0, or only zero segments
            if (!tooltip || tooltip.opacity === 0 || !nonZeroPts?.length) {
              if (hideTooltipTimeoutRef.current) {
                clearTimeout(hideTooltipTimeoutRef.current);
                hideTooltipTimeoutRef.current = null;
              }
              hideTooltipTimeoutRef.current = setTimeout(() => {
                if (!tooltipHoveredRef.current) {
                  tooltipEl.style.opacity = 0;
                  tooltipEl.style.pointerEvents = 'none';
                }
                hideTooltipTimeoutRef.current = null;
              }, tooltipChartDismissDelayMs);
              return;
            }

            if (hideTooltipTimeoutRef.current) {
              clearTimeout(hideTooltipTimeoutRef.current);
              hideTooltipTimeoutRef.current = null;
            }
            if (tooltipLeaveHideTimeoutRef.current) {
              clearTimeout(tooltipLeaveHideTimeoutRef.current);
              tooltipLeaveHideTimeoutRef.current = null;
            }

            const titleLines = tooltip.title || [];
            const titleText = titleLines.join(' ');
            let innerHtml = '';
            if (titleText) {
                innerHtml += `<div style="font-size: 14px; font-weight: bold; margin-bottom: 4px; font-family: 'Inter', sans-serif; pointer-events: none;">${titleText}</div>`;
            }

            innerHtml += `<div>`;
            (tooltip.dataPoints || [])
              .map((dp, idx) => ({ dp, idx }))
              .filter(({ dp }) => dp.parsed?.x != null && Number(dp.parsed.x) !== 0)
              .forEach(({ dp, idx: origIdx }) => {
                const numericValue = Number(dp.parsed.x ?? 0);
                const formattedValue = showPercentInTooltip
                  ? `${numericValue.toFixed(tooltipDecimalPlaces)}%`
                  : `${numericValue.toFixed(tooltipDecimalPlaces)}`;
                let lineText = `${dp.dataset.label}: ${formattedValue}`;
                if (showCountInTooltip && dp.dataset.counts) {
                  const count = dp.dataset.counts[dp.dataIndex];
                  if (count !== undefined) lineText += ` (${count})`;
                }
                const color = (tooltip.labelColors && tooltip.labelColors[origIdx])
                  ? tooltip.labelColors[origIdx].backgroundColor
                  : '#000';
                
                innerHtml += `
                  <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px; font-family: 'Inter', sans-serif;">
                     <span style="display: inline-block; width: 12px; height: 12px; background-color: ${color}; border-radius: 2px; pointer-events: none;"></span>
                     <span 
                       class="tooltip-interactive-text" 
                       data-dataset="${dp.datasetIndex}" 
                       data-index="${dp.dataIndex}"
                       style="font-size: 12px; cursor: pointer; color: white; text-decoration: none; white-space: nowrap;"
                       onmouseover="this.style.textDecoration='underline'"
                       onmouseout="this.style.textDecoration='none'"
                     >
                       ${lineText}
                     </span>
                  </div>
                `;
            });
            innerHtml += `</div>`;

            // Caret positioning logic
            const yAlign = tooltip.yAlign || 'center';
            const xAlign = tooltip.xAlign || 'center';
            const caretX = tooltip.caretX;
            const caretY = tooltip.caretY;

            // Simple caret triangle
            let caretStyle = '';
            if (yAlign === 'bottom') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-top-color: rgba(0,0,0,0.8); border-width: 6px 6px 0 6px; bottom: -6px; left: 50%; transform: translateX(-50%);`;
            } else if (yAlign === 'top') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-bottom-color: rgba(0,0,0,0.8); border-width: 0 6px 6px 6px; top: -6px; left: 50%; transform: translateX(-50%);`;
            } else if (xAlign === 'left') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-right-color: rgba(0,0,0,0.8); border-width: 6px 6px 6px 0; left: -6px; top: 50%; transform: translateY(-50%);`;
            } else if (xAlign === 'right') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-left-color: rgba(0,0,0,0.8); border-width: 6px 0 6px 6px; right: -6px; top: 50%; transform: translateY(-50%);`;
            }
            if (caretStyle) {
                innerHtml += `<div style="${caretStyle} pointer-events: none;"></div>`;
            }

            tooltipEl.innerHTML = innerHtml;
            tooltipEl.style.opacity = 1;
            tooltipEl.style.pointerEvents = 'auto';
            
            // Positioning the box
            tooltipEl.style.left = caretX + 'px';
            tooltipEl.style.top = caretY + 'px';
            
            let translateX = '-50%';
            let translateY = '0';
            let offsetX = 0;
            let offsetY = 0;

            if (yAlign === 'bottom') { translateY = '-100%'; offsetY = -12; }
            else if (yAlign === 'top') { translateY = '0'; offsetY = 12; }
            else { translateY = '-50%'; }

            if (xAlign === 'left') { translateX = '0'; offsetX = 12; }
            else if (xAlign === 'right') { translateX = '-100%'; offsetX = -12; }
            else { translateX = '-50%'; }

            tooltipEl.style.transform = `translate(${translateX}, ${translateY}) translate(${offsetX}px, ${offsetY}px)`;
          }
        },
        datalabels: {
          display: (ctx) => {
            const raw = ctx?.dataset?.data?.[ctx.dataIndex];
            if (raw === null || raw === undefined || raw === "") return false;
            const n = Number(raw);
            return Number.isFinite(n) && n !== 0;
          },
          formatter: (value, context) => {
            let text = "";
            if (typeof datalabelsFormatter === 'function') {
              text = String(datalabelsFormatter(value, context));
            } else {
              text = typeof value === 'number' ? String(Math.round(value)) : String(value ?? "");
            }
            if (context.hovered && (onBarClick || hoverCursor)) {
              // Add a combining underline character to simulate an underline effect
              return text.split('').join('\u0332') + '\u0332';
            }
            return text;
          },
          anchor: datalabelAnchor,
          align: datalabelAlign,
          color: (context) => {
            const hovered = context.hovered && (onBarClick || hoverCursor);
            const hoverCol =
              typeof datalabelHoverColor === "function"
                ? datalabelHoverColor(context)
                : datalabelHoverColor;
            const normalCol =
              typeof datalabelColor === "function"
                ? datalabelColor(context)
                : datalabelColor;
            return hovered ? hoverCol : normalCol;
          },
          font: {
            family: "'Inter', sans-serif",
            size: 10,
            weight: "600",
          },
          listeners: {
            enter: (context) => {
              context.hovered = true;
              context.chart.isDataLabelHovered = true;
              if (onBarClick || hoverCursor) {
                context.chart.canvas.style.cursor = 'pointer';
              }
              return true; // redraws for hover effect
            },
            leave: (context) => {
              context.hovered = false;
              context.chart.isDataLabelHovered = false;
              context.chart.canvas.style.cursor = 'default';
              return true;
            },
            // The plugin passes the chart event as the SECOND argument;
            // context.event does not exist, and without the event's native
            // reference the dedupe in dispatchBarClick cannot engage.
            click: (context, event) => {
              if (onBarClick) {
                dispatchBarClick(event, [{
                  index: context.dataIndex,
                  datasetIndex: context.datasetIndex
                }]);
              }
            }
          },
          clamp: true,
        },
      },
      scales: {
        x: {
          stacked,
          min: 0,
          max: xAxisMax,
          ticks: {
            display: !fixedXAxis,
            stepSize: xAxisStepSize,
            color: "#6B7280",
            font: { family: "'Inter', sans-serif", size: 11 },
            callback: (value) =>
              xAxisFormatter && typeof xAxisFormatter === "function"
                ? xAxisFormatter(value)
                : value,
          },
          grid: { display: false },
          border: { display: !fixedXAxis, color: "#E5E7EB" },
          title: {
            display: !fixedXAxis,
            text: xAxisTitle,
            color: "#D1D5DB",
            font: { family: "'Inter', sans-serif", size: 12 },
          },
        },
        y: {
          stacked,
          grid: { display: false },
          border: { display: true, color: "#E5E7EB" },
          ticks: {
            crossAlign: "far",
            color: "#6B7280",
            font: { family: "'Inter', sans-serif", size: 11 },
          },
          title: { display: false },
        },
      },
    }),
    [
      onBarClick,
      dispatchBarClick,
      hoverCursor,
      datalabelsFormatter,
      datalabelAnchor,
      datalabelAlign,
      datalabelColor,
      datalabelHoverColor,
      showPercentInTooltip,
      showCountInTooltip,
      tooltipDecimalPlaces,
      tooltipChartDismissDelayMs,
      stacked,
      xAxisMax,
      xAxisStepSize,
      xAxisFormatter,
      fixedXAxis,
      xAxisTitle,
      showYAxisTitle,
    ],
  );

  const resolvedFixedXAxisTicks = useMemo(() => {
    if (Array.isArray(fixedXAxisTicks) && fixedXAxisTicks.length > 0) {
      return fixedXAxisTicks;
    }
    if (!xAxisStepSize || !xAxisMax) return [0];
    const ticks = [];
    for (let v = 0; v <= xAxisMax; v += xAxisStepSize) ticks.push(v);
    return ticks;
  }, [fixedXAxisTicks, xAxisStepSize, xAxisMax]);

  return (
    <div className={styles.container}>
      <Box
        sx={{
          width: "100%",
          bgcolor: "white",
          borderRadius: "32px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          border: "1px solid #E9DFFA",
          pt: paddingTop,
          pr: paddingRight,
          pb: paddingBottom,
          pl: paddingLeft,
          minHeight: cardMinHeight,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "between",
            alignItems: "start",
            marginBottom: legendPlacement === "titleRight" ? "0px" : "24px",
            gap: legendPlacement === "titleRight" ? "0px" : "16px",
          }}>
          <div className="flex-1 w-full">
            <div
              style={{
                display: "flex",
                justifyContent: legendPlacement === "titleRight" ? "space-between" : "flex-start",
                alignItems: "center",
                gap: "12px",
                marginBottom: legendPlacement === "titleRight" ? "5px" : "16px",
              }}
            >
              <h2
                style={{
                  fontSize: titleTextSize,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  lineHeight: "100%",
                  color: "#22C55E",
                }}
              >
                {title}
              </h2>
              {legendPlacement === "titleRight" && legendItems?.length > 0 && (
                <div
                  className={styles.legendBox}
                  style={{ border: "0.5px solid #D9D9D9", background: "transparent" }}
                >
                  {legendItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span
                        className="block w-3.5 h-3.5 rounded-sm"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-[11px] font-medium text-gray-400">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(legendPlacement !== "titleRight" && legendItems?.length > 0) ||
            (topRightAction && topRightActionPlacement === "legendActionRow") ? (
              <div className={styles.legendActionRow}>
                {legendPlacement !== "titleRight" && legendItems?.length > 0 && (
                  <div
                    className={styles.legendBox}
                    style={{ border: "0.5px solid #D9D9D9", background: "transparent" }}
                  >
                    {legendItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <span
                          className="block w-3 h-3 rounded-sm"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-[11px] font-medium text-gray-400">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {topRightAction && topRightActionPlacement === "legendActionRow" && (
                  <div className="flex-shrink-0">{topRightAction}</div>
                )}
              </div>
            ) : null}

            <div className={styles.breadcrumbsRow}>
              <div className={styles.breadcrumbsRail}>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    <span
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "13px",
                        lineHeight: "20px",
                        color: "#4841B9",
                        fontStyle: "italic",
                        fontWeight: crumb.active ? 600 : 400,
                      }}
                      className={`transition-colors ${crumb.onClick ? "cursor-pointer hover:opacity-80" : ""}`}
                      onClick={crumb.onClick}
                    >
                      {crumb.label}
                    </span>
                    {index < breadcrumbs.length - 1 && (
                      <span className="text-[#4841B9] font-medium">&gt;</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {topRightAction &&
                topRightActionPlacement === "breadcrumbs" && (
                  <div className="flex-shrink-0">{topRightAction}</div>
                )}
            </div>
          </div>
        </div>

        <div className={styles.graphWrapper} style={{ height: chartHeight }}>
          {showYAxisTitle && yAxisTitle && (
            <div
              className="mb-1 pl-7 text-[12px] text-gray-300"
              style={{
                fontFamily: "Inter, sans-serif",
               
              }}
            >
              {yAxisTitle}
            </div>
          )}
          <div
            ref={chartScrollRef}
            className={styles.chartOuter}
            style={{ overflowY: shouldScrollY ? "auto" : "hidden" }}
          >
            <div style={{ height: `${innerChartHeightPx}px`, minHeight: "100%", position: 'relative' }}>
              <Bar
                data={data}
                options={options}
                plugins={[chartAreaSyncPlugin, yAxisCategoryPlugin, minBarLengthPlugin, stackEndLabelPlugin, ChartDataLabels]}
              />
              <div 
                ref={setTooltipRef}
                style={{
                  position: 'absolute',
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.1s ease',
                  zIndex: 9999,
                  boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
                  whiteSpace: 'nowrap',
                }}
              />
            </div>
          </div>
          {fixedXAxis && (
            <div>
              <div
                className="border-t border-[#E5E7EB]"
                style={{
                  marginLeft: `${chartAreaBounds.left}px`,
                  width: `${Math.max(chartAreaBounds.right - chartAreaBounds.left, 0)}px`,
                }}
              >
                <div className="flex items-center justify-between pt-2">
                  {resolvedFixedXAxisTicks.map((tick) => (
                    <span
                      key={tick}
                      className="text-[11px] text-[#6B7280]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {xAxisFormatter && typeof xAxisFormatter === "function"
                        ? xAxisFormatter(tick)
                        : tick}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className="text-[12px] text-[#D1D5DB] text-right mt-5"
                style={{
                  width: `${Math.max(chartAreaBounds.right - chartAreaBounds.left, 0)}px`,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {xAxisTitle}
              </div>

            </div>
          )}
        </div>
      </Box>
    </div>
  );
};

export default CustomHorizontalBarChart;
